/**
 * =============================================================================
 * SUMMONS — Tipos e interfaces del sistema de invocaciones
 * =============================================================================
 *
 * Un summon es una instancia de `NPC` con `ownerId` (sessionId del invocador) +
 * un `summonProfileId`. El perfil (catálogo en `shared/data/summonProfiles.ts`)
 * describe TODO sobre la invocación; cada FUENTE (spell toggle, item equipado,
 * item usable, consumible) sólo referencia un `summonProfileId`.
 *
 * Ver docs/SUMMONS.md.
 */

import type { ItemModifiers } from './item';

// =============================================================================
// MODO DE ATAQUE (ciclable por tecla)
// =============================================================================

/**
 * Cómo se comportan los summons hostiles del jugador frente a los enemigos.
 * Ciclable con la tecla `cycleSummonMode`. De fábrica sólo se cicla
 * `defensive`↔`passive`; `aggressive` requiere el mod único `summonAggressiveMode`
 * (amuleto) o un keystone del árbol.
 */
export type SummonMode =
  /** Sólo atacan a quien ataca al OWNER (o al propio summon). */
  | 'defensive'
  /** Atacan lo que el OWNER ataca (melee/distancia/magia) + a quien los ataque. */
  | 'passive'
  /** Atacan a cualquier enemigo que se cruce (aggro autónomo). */
  | 'aggressive';

export const SUMMON_MODES: readonly SummonMode[] = ['defensive', 'passive', 'aggressive'] as const;

/** Modo base disponible sin desbloqueos (defensive↔passive). */
export const SUMMON_MODES_BASE: readonly SummonMode[] = ['defensive', 'passive'] as const;

export const SUMMON_MODE_LABEL_ES: Record<SummonMode, string> = {
  defensive: 'Defensivo',
  passive: 'Pasivo',
  aggressive: 'Agresivo',
};

// =============================================================================
// ROL DEL SUMMON
// =============================================================================

/**
 * `hostile` = pega a enemigos (mob AI dirigido).
 * `support` = NO ataca; castea hechizos sobre el OWNER (curar, liberar, buffear,
 * dar maná) con cadencia/cooldown propio.
 */
export type SummonRole = 'hostile' | 'support';

// =============================================================================
// FX HOOKS (client-side puro, catálogo fx_*)
// =============================================================================

export interface SummonFxHooks {
  /** One-shot sobre el OWNER al empezar la invocación (canal). */
  channelStart?: string;
  /** Persistente sobre el OWNER MIENTRAS canaliza (barra de invocación). */
  channel?: string;
  /** One-shot sobre el tile de spawn al completar la invocación (portal). */
  channelEnd?: string;
  /** Persistente sobre la entidad invocada mientras vive (aura de summon). */
  onEntity?: string;
  /** One-shot sobre la entidad al morir/disiparse. */
  onDeath?: string;
}

// =============================================================================
// PERFIL DE SUMMON
// =============================================================================

export interface SummonProfile {
  /** ID interno en inglés (ej: 'summon_wolf'). */
  id: string;
  /** Display en español. */
  nameEs: string;
  /** Template de NPC (shared/data/npcs.ts) a spawnear. */
  npcTemplateId: string;
  /** Cantidad de entidades que invoca el proceso (antes de bonus `summonPackLeader`). */
  count: number;
  /** hostile (pega) | support (asiste al owner). */
  role: SummonRole;
  /** ms de canalización antes de que aparezca (0 = instantáneo). */
  castTimeMs: number;
  /**
   * ms de duración. `0` = permanente: vive hasta que el owner lo disipe
   * (toggle off / desequipar) o muera; si muere y `respawnMs > 0`, revive.
   * `> 0` = duración fija (summon burst), se disipa al expirar.
   */
  durationMs: number;
  /**
   * Reserva de maná (0..1 de maxMana) mientras esté activo. Sólo para el modelo
   * toggle (spells del brujo). Se aplica como buff `summon_reserve_*` igual que las auras.
   */
  manaReservePercent?: number;
  /**
   * ms hasta revivir si el summon permanente muere. `0` = no revive (hay que
   * reinvocar). Reducido por el stat `summonResurrectionSpeed` del owner.
   */
  respawnMs: number;
  /** Buff al OWNER mientras el summon esté activo (ej: +daño). */
  ownerBuff?: ItemModifiers;
  /** Debuff al OWNER mientras esté activo (trade-off de build). */
  ownerDebuff?: ItemModifiers;
  /** role=support: spells que castea sobre el owner (cada uno con su cooldown). */
  supportSpellIds?: string[];
  /** role=support: cadencia mínima entre intentos de casteo de soporte (ms). */
  supportIntervalMs?: number;
  /**
   * role=support: si está seteado, el summon PROVOCA (taunt) a los enemigos cercanos a
   * SÍ MISMO con esta cadencia — jala el aggro hacia él (tanque provocador). Ver docs/TAUNT.md.
   */
  taunt?: { radius: number; intervalMs: number; durationMs: number };
  /**
   * role=support: si está seteado, el summon CASTEA este hechizo en ÁREA autocentrada
   * (sobre su propia posición) con esta cadencia — usado por las invocaciones detectoras
   * (Pulso Revelador). A diferencia de `supportSpellIds` (que apunta al owner), esto
   * ejecuta un cast real vía executeSpell. Ver docs/INVISIBILITY.md.
   */
  pulseSpellId?: string;
  /** role=support: cadencia (ms) del `pulseSpellId`. Default 10000. */
  pulseIntervalMs?: number;
  /** Hooks de partículas del ciclo de vida (client-side). */
  fx?: SummonFxHooks;
}
